import { useCallback, useEffect, useState } from "react";
import {
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  Check,
  FileText,
  Lightning,
  ListChecks,
  Plus,
  Trash,
  Warning,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  getManagementItems,
  createManagementItem,
  updateManagementItem,
  archiveManagementItem,
  getWeeklyUpdates,
  submitWeeklyUpdate,
  triggerAiRefresh,
  generateReport,
  getReportUrl,
  getManagementSettings,
  updateManagementSettings,
} from "../../../api/projectManagement";

// ── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "risk", label: "Risks", icon: Warning },
  { key: "issue", label: "Issues", icon: WarningCircle },
  { key: "task", label: "Tasks & WBS", icon: ListChecks },
  { key: "updates", label: "Weekly Updates", icon: ArrowsClockwise },
];

const PRIORITY_BADGES = {
  low: "bg-slate-50 text-slate-600 border-slate-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_BADGES = {
  open: { label: "Open", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed", className: "bg-slate-50 text-slate-500 border-slate-200" },
  archived: { label: "Archived", className: "bg-slate-50 text-slate-400 border-slate-200" },
};

const UPDATE_STATUS_BADGES = {
  pending: { label: "Processing…", className: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_approval: { label: "Awaiting Approval", className: "bg-blue-50 text-blue-700 border-blue-200" },
  processed: { label: "Processed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Failed", className: "bg-red-50 text-red-600 border-red-200" },
};

// ── Small UI Components ──────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.open;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function PriorityBadge({ level }) {
  if (!level) return null;
  const cls = PRIORITY_BADGES[level] || PRIORITY_BADGES.low;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      {level}
    </span>
  );
}

function CompletionBar({ percentage }) {
  return (
    <div className="flex items-center gap-2 flex-1 max-w-[120px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-[10px] text-slate-500 w-7 text-right">{percentage}%</span>
    </div>
  );
}

// ── Item Row ─────────────────────────────────────────────────────────────────

function ManagementItemRow({ item, projectId, onUpdate, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (field, currentValue) => {
    setEditing(field);
    setEditVal(currentValue || "");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditVal("");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await updateManagementItem(projectId, item.id, { [editing]: editVal });
      onUpdate(updated);
      toast.success(`Updated ${editing.replace("_", " ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
      cancelEdit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveEdit();
    if (e.key === "Escape") cancelEdit();
  };

  const renderField = (label, field, value) => (
    <div className="flex items-start gap-2 text-xs" key={field}>
      <span className="w-32 flex-shrink-0 font-medium text-slate-500">{label}</span>
      {editing === field ? (
        <div className="flex items-center gap-1 flex-1">
          <input
            autoFocus
            className="flex-1 text-xs border border-slate-200 rounded px-2 py-0.5 outline-none focus:border-primary bg-white"
            value={editVal}
            onChange={(e) => setEditVal(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button onClick={saveEdit} disabled={saving} className="text-primary hover:text-primary/80">
            <Check size={12} weight="bold" />
          </button>
          <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-700">
            <X size={12} />
          </button>
        </div>
      ) : (
        <span
          className="text-slate-700 cursor-pointer hover:text-primary flex-1"
          onClick={() => startEdit(field, value)}
        >
          {value || <span className="text-slate-300 italic">—</span>}
        </span>
      )}
    </div>
  );

  const isTask = item.record_type === "task";
  const isRisk = item.record_type === "risk";
  const isIssue = item.record_type === "issue";
  const indent = item.parent_id ? "ml-6" : "";

  return (
    <div className={`border border-slate-200 rounded ${indent}`}>
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <CaretDown size={12} className="text-slate-400" />
        ) : (
          <CaretRight size={12} className="text-slate-400" />
        )}
        {item.milestone && <Lightning size={12} className="text-amber-500" weight="fill" />}
        <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{item.title}</span>
        {isTask && <CompletionBar percentage={item.completion_percentage || 0} />}
        {item.due_date && (
          <span className="text-[10px] text-slate-400 hidden sm:inline">{item.due_date}</span>
        )}
        <PriorityBadge level={item.priority || item.severity || item.likelihood} />
        <StatusBadge status={item.status} />
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2 bg-slate-50/50">
          {renderField("Title", "title", item.title)}
          {renderField("Description", "description", item.description)}
          {renderField("Owner", "owner", item.owner)}
          {renderField("Category", "category", item.category)}

          {isRisk && (
            <>
              {renderField("Likelihood", "likelihood", item.likelihood)}
              {renderField("Impact", "impact", item.impact)}
              {renderField("Mitigation", "mitigation_notes", item.mitigation_notes)}
            </>
          )}

          {isIssue && (
            <>
              {renderField("Severity", "severity", item.severity)}
              {renderField("Resolution", "resolution_notes", item.resolution_notes)}
            </>
          )}

          {isTask && (
            <>
              {renderField("Due Date", "due_date", item.due_date)}
              {renderField("Priority", "priority", item.priority)}
              {renderField("Completion %", "completion_percentage", String(item.completion_percentage || 0))}
              {item.estimated_hours != null && (
                <div className="flex items-start gap-2 text-xs">
                  <span className="w-32 flex-shrink-0 font-medium text-slate-500">Est. Hours</span>
                  <span className="text-slate-700">{item.estimated_hours}h</span>
                </div>
              )}
            </>
          )}

          <div className="pt-2 border-t border-slate-100 flex items-center justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive(item);
              }}
              className="flex items-center gap-1 text-[10px] font-semibold text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 px-2 py-1 rounded transition-colors"
            >
              <Trash size={10} /> Archive
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Item Form ────────────────────────────────────────────────────────────

function AddItemForm({ projectId, recordType, onCreated, onCancel }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);

  // Type-specific fields
  const [likelihood, setLikelihood] = useState("");
  const [impact, setImpact] = useState("");
  const [severity, setSeverity] = useState("");
  const [priority, setPriority] = useState("");
  const [dueDate, setDueDate] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const data = {
        record_type: recordType,
        title: title.trim(),
        description: description.trim(),
        owner: owner.trim(),
      };
      if (recordType === "risk") {
        if (likelihood) data.likelihood = likelihood;
        if (impact) data.impact = impact;
      }
      if (recordType === "issue") {
        if (severity) data.severity = severity;
      }
      if (recordType === "task") {
        if (priority) data.priority = priority;
        if (dueDate) data.due_date = dueDate;
      }
      const created = await createManagementItem(projectId, data);
      toast.success(`${recordType} created`);
      onCreated(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-slate-200 rounded p-3 space-y-2 bg-slate-50">
      <input
        autoFocus
        required
        className="w-full text-sm border border-slate-200 rounded px-2.5 py-1.5 outline-none focus:border-primary bg-white"
        placeholder="Title (required)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white resize-none"
        placeholder="Description (optional)"
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
          placeholder="Owner"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
        />
        {recordType === "risk" && (
          <>
            <select
              className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
              value={likelihood}
              onChange={(e) => setLikelihood(e.target.value)}
            >
              <option value="">Likelihood…</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <select
              className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
            >
              <option value="">Impact…</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </>
        )}
        {recordType === "issue" && (
          <select
            className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="">Severity…</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        )}
        {recordType === "task" && (
          <>
            <select
              className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="">Priority…</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <input
              type="date"
              className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          <Check size={12} weight="bold" /> {saving ? "Creating…" : "Create"}
        </button>
      </div>
    </form>
  );
}

// ── Items Tab Content ────────────────────────────────────────────────────────

function ItemsTabContent({ projectId, recordType }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const data = await getManagementItems(projectId, recordType, "open");
      setItems(data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [projectId, recordType]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleCreated = (newItem) => {
    setItems((prev) => [newItem, ...prev]);
    setShowAdd(false);
  };

  const handleUpdate = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    try {
      await archiveManagementItem(projectId, archiveTarget.id);
      setItems((prev) => prev.filter((i) => i.id !== archiveTarget.id));
      toast.success(`"${archiveTarget.title}" archived`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    } finally {
      setArchiveTarget(null);
    }
  };

  if (loading) return <div className="text-xs text-slate-400 py-4">Loading…</div>;

  // For tasks, render hierarchy (parent items first, children indented)
  const sortedItems = recordType === "task"
    ? [...items].sort((a, b) => {
        // Parents first, then children grouped by parent
        if (!a.parent_id && b.parent_id) return -1;
        if (a.parent_id && !b.parent_id) return 1;
        return 0;
      })
    : items;

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition-colors"
        >
          <Plus size={10} weight="bold" /> Add {recordType}
        </button>
      </div>

      {showAdd && (
        <div className="mb-3">
          <AddItemForm
            projectId={projectId}
            recordType={recordType}
            onCreated={handleCreated}
            onCancel={() => setShowAdd(false)}
          />
        </div>
      )}

      {sortedItems.length === 0 && !showAdd ? (
        <p className="text-xs text-slate-400 italic py-4">
          No {recordType}s yet. Add one to start tracking.
        </p>
      ) : (
        <div className="space-y-2">
          {sortedItems.map((item) => (
            <ManagementItemRow
              key={item.id}
              item={item}
              projectId={projectId}
              onUpdate={handleUpdate}
              onArchive={setArchiveTarget}
            />
          ))}
        </div>
      )}

      {archiveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full mx-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Archive item?</h4>
            <p className="text-xs text-slate-600 mb-4">
              This will archive <strong>"{archiveTarget.title}"</strong>. It won't appear in the active list but data is preserved.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setArchiveTarget(null)}
                className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                className="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded transition-colors"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Weekly Updates Tab ────────────────────────────────────────────────────────

function WeeklyUpdatesTab({ projectId }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateText, setUpdateText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [autoApply, setAutoApply] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const loadUpdates = useCallback(async () => {
    try {
      const data = await getWeeklyUpdates(projectId);
      setUpdates(data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadSettings = useCallback(async () => {
    try {
      const settings = await getManagementSettings(projectId);
      setAutoApply(settings.auto_apply_reviews || false);
    } catch {
      // Silent fail — default to false
    } finally {
      setSettingsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadUpdates();
    loadSettings();
  }, [loadUpdates, loadSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!updateText.trim()) return;
    setSubmitting(true);
    try {
      const created = await submitWeeklyUpdate(projectId, updateText.trim());
      setUpdates((prev) => [created, ...prev]);
      setUpdateText("");
      toast.success("Weekly update submitted — AI processing in background");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleAutoApply = async () => {
    const newValue = !autoApply;
    setAutoApply(newValue);
    try {
      await updateManagementSettings(projectId, { auto_apply_reviews: newValue });
      toast.success(newValue ? "Auto-apply enabled" : "Auto-apply disabled");
    } catch (e) {
      setAutoApply(!newValue); // Revert on failure
      toast.error("Failed to update setting");
    }
  };

  if (loading) return <div className="text-xs text-slate-400 py-4">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Auto-apply setting */}
      {!settingsLoading && (
        <div className="flex items-center justify-between border border-slate-200 rounded px-3 py-2 bg-slate-50/50">
          <div>
            <p className="text-xs font-semibold text-slate-700">Auto-apply AI review changes</p>
            <p className="text-[10px] text-slate-500">
              When enabled, nightly AI reviews will automatically apply conservative changes.
            </p>
          </div>
          <button
            onClick={handleToggleAutoApply}
            className={`relative w-9 h-5 rounded-full transition-colors ${
              autoApply ? "bg-primary" : "bg-slate-300"
            }`}
            aria-label="Toggle auto-apply AI reviews"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                autoApply ? "translate-x-4" : ""
              }`}
            />
          </button>
        </div>
      )}

      {/* Submit form */}
      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          className="w-full text-sm border border-slate-200 rounded px-3 py-2 outline-none focus:border-primary bg-white resize-none"
          placeholder="What happened this week? The AI will use this to update risks, issues, and tasks…"
          rows={3}
          value={updateText}
          onChange={(e) => setUpdateText(e.target.value)}
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !updateText.trim()}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            <Check size={12} weight="bold" /> {submitting ? "Submitting…" : "Submit Update"}
          </button>
        </div>
      </form>

      {/* Past updates */}
      {updates.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No weekly updates yet.</p>
      ) : (
        <div className="space-y-3">
          {updates.map((u) => (
            <WeeklyUpdateCard key={u.id} update={u} />
          ))}
        </div>
      )}
    </div>
  );
}

function WeeklyUpdateCard({ update }) {
  const [expanded, setExpanded] = useState(false);
  const badge = UPDATE_STATUS_BADGES[update.status] || UPDATE_STATUS_BADGES.pending;
  const hasChanges = update.ai_changes_applied && update.ai_changes_applied.length > 0;

  return (
    <div className="border border-slate-200 rounded">
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <CaretDown size={12} className="text-slate-400" />
        ) : (
          <CaretRight size={12} className="text-slate-400" />
        )}
        <span className="text-xs text-slate-600 flex-1 truncate">
          {update.update_text.slice(0, 80)}{update.update_text.length > 80 ? "…" : ""}
        </span>
        <span className="text-[10px] text-slate-400">
          {new Date(update.created_at).toLocaleDateString()}
        </span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-slate-50/50">
          <p className="text-xs text-slate-700 whitespace-pre-wrap">{update.update_text}</p>

          {hasChanges && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                AI Changes Applied
              </p>
              {update.ai_changes_applied.map((change, idx) => (
                <div key={idx} className="text-xs text-slate-600 pl-2 border-l-2 border-primary/30">
                  <span className="font-medium">{change.action}</span>
                  {change.title && `: ${change.title}`}
                  {change.field && ` — ${change.field}: ${change.new_value}`}
                  {change.reasoning && (
                    <span className="text-slate-400 ml-1">({change.reasoning})</span>
                  )}
                  {change.error && (
                    <span className="text-red-500 ml-1">Error: {change.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Section Component ───────────────────────────────────────────────────

export function ProjectManagementSection({ project }) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("risk");
  const [refreshing, setRefreshing] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  const projectId = project?.id;

  const handleAiRefresh = async () => {
    setRefreshing(true);
    try {
      await triggerAiRefresh(projectId);
      toast.success("AI refresh started — items will update shortly");
      // Reload after a brief delay to let background task work
      setTimeout(() => {
        setRefreshing(false);
        // Force re-render of tab content by toggling tab
        setActiveTab((t) => t);
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to trigger AI refresh");
      setRefreshing(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const result = await generateReport(projectId);
      if (result.report_id) {
        const url = await getReportUrl(projectId, result.report_id);
        window.open(url, "_blank");
        toast.success("Report generated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="project-management-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
        >
          {collapsed ? <CaretRight size={10} /> : <CaretDown size={10} />}
          <ListChecks size={12} className="text-slate-400" />
          Project Management
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition-colors disabled:opacity-40"
            >
              <FileText size={10} />
              {generatingReport ? "Generating…" : "Generate Report"}
            </button>
            <button
              onClick={handleAiRefresh}
              disabled={refreshing}
              className="flex items-center gap-1 text-[10px] font-semibold text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/50 px-2 py-1 rounded transition-colors disabled:opacity-40"
            >
              <ArrowsClockwise size={10} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Updating…" : "Update from Project Data"}
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-slate-100 pb-2">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-t transition-colors ${
                    isActive
                      ? "font-semibold text-primary border-b-2 border-primary bg-primary/5"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Icon size={12} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === "updates" ? (
            <WeeklyUpdatesTab projectId={projectId} />
          ) : (
            <ItemsTabContent key={activeTab} projectId={projectId} recordType={activeTab} />
          )}
        </>
      )}
    </div>
  );
}
